import React, { useState, useEffect, useRef, useCallback } from "react";
import type { Partner, Suggestion } from "../types";
import { useData } from "../hooks/useData";
import { api } from "../api/client";
import { I } from "./Icons";
import { Avatar, clsx, relDue } from "./Primitives";
import { AcceptDialog, SavedIntel, FullStory, radarTabClass } from "./RadarSave";

interface SyncState {
  gmail: { running: boolean; phase: string; processed: number; total: number; error: string | null; doneAt: number | null };
  intel: { running: boolean; phase: string; articles: number; suggestions: number; error: string | null; doneAt: number | null };
  counts: { emails: number; suggestions: number };
}

const SRC_LABEL: Record<string, string> = {
  signal: "Intelligence",
  intel: "Saga Intelligence",
  email: "Email",
  minutes: "Minutes",
};

interface RadarProps {
  go: (screen: string, id?: string) => void;
  bump: () => void;
  partners: Record<string, Partner>;
  me: string;
}

function SuggCard({
  s,
  accept,
  deny,
  go,
  reviewed,
  partners,
}: {
  s: Suggestion;
  accept: (s: Suggestion) => void;
  deny: (id: string) => void;
  go: (screen: string, id?: string) => void;
  reviewed?: boolean;
  partners: Record<string, Partner>;
}) {
  const owner = s.owner ? partners[s.owner] : null;
  const isTask = s.kind === "task";
  const conf = Math.round(s.confidence * 100);
  const [busy, setBusy] = useState(false);
  // Reset busy in finally: on a server error the card stays in the list (status
  // rolled back to 'new'), so without this the buttons would be stuck disabled.
  const doAccept = () => accept(s);
  const doDeny = async () => { if (busy) return; setBusy(true); try { await deny(s.id); } finally { setBusy(false); } };

  return (
    <div className={clsx("sugg-card", "sg-" + s.kind, s.status !== "new" && "sg-" + s.status)}>
      <span className={clsx("sugg-ic", isTask ? "sugg-task" : "sugg-info")}>
        {isTask ? I.check({ size: 15, w: 1.9 }) : I.signal({ size: 15, w: 1.9 })}
      </span>
      <div className="sugg-body">
        <div className="sugg-top">
          <span className={clsx("sugg-kind", isTask ? "k-task" : "k-info")}>
            {isTask ? "Add task" : "Add information"}
          </span>
          <span className="sugg-src">
            {SRC_LABEL[s.source_kind] || s.source_kind} &middot; {s.source_label}
          </span>
          <span className="sugg-conf mono">{conf}%</span>
        </div>
        {isTask ? (
          <div className="sugg-title">{s.task}</div>
        ) : (
          <>
            {s.country && <div className="sugg-country">{s.country}</div>}
            <div className="sugg-title">{s.title}</div>
            <div className="sugg-text">{s.text}</div>
            {s.relevance && <div className="sugg-rel"><strong>Saga relevance:</strong> {s.relevance}</div>}
            {(s.source_kind === "intel" || s.url) && <FullStory kind="suggestions" id={s.id} url={s.url} />}
          </>
        )}
        <div className="sugg-route">
          {isTask && owner && (
            <span className="sugg-assign">
              <Avatar partner={owner} size={18} />
              {owner.name.split(" ")[0]}
            </span>
          )}
          {isTask && s.due && (
            <span className="sugg-due mono">
              {I.clock({ size: 11 })}
              {relDue(s.due)}
            </span>
          )}
          {I.arrowRight({ size: 13, w: 1.8, style: { color: "var(--faint)" } })}
          <button className="sugg-deal" onClick={() => s.deal_id && go("project", s.deal_id)}>
            {s.deal_name || (s.status === "new" ? "you choose where it goes" : "not linked to a deal")}
          </button>
        </div>
      </div>

      {s.status === "new" ? (
        <div className="sugg-actions">
          <button className="sugg-deny" onClick={doDeny} disabled={busy} title="Dismiss">
            {I.plus({ size: 16, style: { transform: "rotate(45deg)" } })}
          </button>
          <button className="sugg-accept" onClick={doAccept} disabled={busy} title="Choose where to save it">
            {I.check({ size: 16, w: 2.4 })}
            Save…
          </button>
        </div>
      ) : (
        <span className={clsx("sugg-status", "ss-" + s.status)}>
          {s.status === "accepted" ? (
            <>
              {I.check({ size: 13, w: 2.4 })}
              {isTask ? "Added to tasks" : "Saved to CRM"}
            </>
          ) : (
            "Dismissed"
          )}
        </span>
      )}
    </div>
  );
}

interface AiCosts {
  monthly_total_usd: number;
  calls: number;
  by_purpose: Array<{ purpose: string; calls: number; cost: number }>;
}

export default function Radar({ go, bump, partners, me }: RadarProps) {
  const { data: all, refetch } = useData<Suggestion[]>("/api/suggestions");
  const { data: aiCosts } = useData<AiCosts>("/api/ai/costs");
  const [showReviewed, setShowReviewed] = useState(false);
  const [syncState, setSyncState] = useState<SyncState | null>(null);
  const polling = useRef<ReturnType<typeof setInterval> | null>(null);
  const startEmails = useRef(0);

  const stopPolling = useCallback(() => {
    if (polling.current) { clearInterval(polling.current); polling.current = null; }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const pollStatus = useCallback(async () => {
    try {
      const s = await api.get<SyncState>("/api/sync/status");
      setSyncState(s);
      if (!s.gmail.running && !s.intel.running) {
        stopPolling();
        refetch();
        bump();
      }
    } catch { /* ignore */ }
  }, [refetch, bump, stopPolling]);

  const syncNow = async () => {
    try {
      const initial = await api.get<SyncState>("/api/sync/status");
      startEmails.current = initial.counts.emails;
      setSyncState({ ...initial, gmail: { ...initial.gmail, running: true, phase: "starting" }, intel: { ...initial.intel, running: true, phase: "starting" } });
    } catch { /* proceed to trigger sync regardless */ }
    api.post("/api/sync/gmail", {}).catch(() => {});
    api.post("/api/sync/intel", {}).catch(() => {});
    stopPolling();
    polling.current = setInterval(pollStatus, 2000);
  };

  const [diagnosing, setDiagnosing] = useState(false);
  const [diagResult, setDiagResult] = useState<string | null>(null);

  const runDiagnose = async () => {
    setDiagnosing(true);
    setDiagResult(null);
    try {
      const r = await api.get<{
        suggestions: { total: number; new: number };
        lastPull: { articles_fetched: number; suggestions_created: number; errors: string[] | null; run_at: string } | string;
        activeDeals: number;
        markets: string[];
        infoApiUrl: string;
        infoApiHealth: string;
        xaiConfigured: boolean;
        xaiModel: string;
      }>("/api/sync/diagnose-intel");
      const lines: string[] = [];
      lines.push(`Suggestions in DB: ${r.suggestions.total} total, ${r.suggestions.new} new`);
      if (typeof r.lastPull === "string") {
        lines.push(`Last pull: ${r.lastPull}`);
      } else {
        lines.push(`Last pull: ${r.lastPull.articles_fetched} articles, ${r.lastPull.suggestions_created} suggestions (${new Date(r.lastPull.run_at).toLocaleString()})`);
        if (r.lastPull.errors?.length) lines.push(`  Errors: ${r.lastPull.errors.join("; ")}`);
      }
      lines.push(`Active deals: ${r.activeDeals}, markets: ${r.markets.join(", ") || "none"}`);
      lines.push(`Info API (${r.infoApiUrl}): ${r.infoApiHealth}`);
      lines.push(`xAI: ${r.xaiConfigured ? "configured" : "NOT configured"}, model: ${r.xaiModel}`);
      setDiagResult(lines.join("\n"));
    } catch (err) {
      setDiagResult("Diagnose failed: " + String(err));
    }
    setDiagnosing(false);
  };

  const isRunning = syncState?.gmail.running || syncState?.intel.running;
  const emailsDelta = syncState ? syncState.counts.emails - startEmails.current : 0;

  let syncLabel = "";
  if (syncState && isRunning) {
    const parts: string[] = [];
    if (syncState.gmail.running) parts.push(syncState.gmail.phase);
    if (syncState.intel.running) parts.push("intel: " + syncState.intel.phase);
    syncLabel = parts.join(" · ");
    if (emailsDelta > 0) syncLabel += ` — ${emailsDelta} new`;
  } else if (syncState && !isRunning && syncState.gmail.doneAt) {
    syncLabel = emailsDelta > 0 ? `Done — ${emailsDelta} new emails` : "Done — no new emails";
  }

  const suggestions = all || [];
  const newOnes = suggestions.filter((s) => s.status === "new");
  const reviewed = suggestions.filter((s) => s.status !== "new");

  const [accepting, setAccepting] = useState<Suggestion | null>(null);
  const [tab, setTab] = useState<"review" | "saved">("review");
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const accept = (s: Suggestion) => setAccepting(s);

  const deny = async (id: string) => {
    try {
      await api.put("/api/suggestions/" + id + "/dismiss", {});
    } catch { /* ignore */ }
    refetch();
    bump();
  };

  return (
    <div className="screen radar2">
      <div className="radar2-head">
        <div>
          <h1 className="page-title">Radar</h1>
          <div className="page-sub">
            <span className="ai-chip">
              {I.ai({ size: 12, w: 1.8 })}
              Saga AI
            </span>
            Saga's stories from info.saga-advisory.com — save what matters to a deal, account or market, or dismiss
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <div style={{ display: "flex", gap: 4 }}>
              <button className="btn ghost sm" onClick={syncNow} disabled={!!isRunning}>
                {I.radar({ size: 14, w: 1.8 })} {isRunning ? "Syncing…" : "Sync now"}
              </button>
              <button className="btn ghost sm" onClick={runDiagnose} disabled={diagnosing} style={{ fontSize: 11 }}>
                {diagnosing ? "Testing…" : "Diagnose"}
              </button>
            </div>
            {syncLabel && (
              <span style={{ fontSize: 11, color: isRunning ? "var(--ember)" : "var(--muted)", fontFamily: "var(--font-mono)" }}>
                {syncLabel}
              </span>
            )}
          </div>
          <div className="radar2-count">
            <span className="r2-n mono">{newOnes.length}</span>
            <span className="r2-l">to review</span>
          </div>
        </div>
      </div>

      <div className="seg rs-tabs">
        <button className={radarTabClass(tab === "review")} onClick={() => setTab("review")}>To review <span className="ld-n">{newOnes.length}</span></button>
        <button className={radarTabClass(tab === "saved")} onClick={() => setTab("saved")}>Saved intel</button>
      </div>
      {savedMsg && <div className="set-notice ok" style={{ margin: "0 0 12px" }}>{savedMsg}</div>}
      {accepting && (
        <AcceptDialog s={accepting} partners={partners} me={me} onClose={() => setAccepting(null)}
          onSaved={msg => { setAccepting(null); setSavedMsg(msg); setTimeout(() => setSavedMsg(null), 6000); refetch(); bump(); }} />
      )}
      {tab === "saved" && <SavedIntel go={go} />}
      {tab === "review" && diagResult && (
        <div style={{ whiteSpace: "pre-wrap", fontSize: 12, padding: "8px 12px", background: diagResult.includes("FAIL") || diagResult.includes("NOT") ? "#fef2f2" : "#f0fdf4", border: "1px solid", borderColor: diagResult.includes("FAIL") || diagResult.includes("NOT") ? "#fca5a5" : "#86efac", borderRadius: 8, marginBottom: 12, color: "#333" }}>
          {diagResult}
          <button onClick={() => setDiagResult(null)} style={{ marginLeft: 12, cursor: "pointer", background: "none", border: "none", color: "#999" }}>x</button>
        </div>
      )}

      {tab !== "review" ? null : newOnes.length === 0 ? (
        <div className="radar2-clear">
          <div className="r2-clear-ic">{I.check({ size: 26, w: 2.4 })}</div>
          <div className="r2-clear-t">All clear, captain</div>
          <div className="r2-clear-s">
            Nothing on the radar. New intel, email insights and meeting follow-ups will pop up here when the AI spots something.
          </div>
        </div>
      ) : (
        <div className="sugg-list">
          {newOnes.map((s) => (
            <SuggCard key={s.id} s={s} accept={accept} deny={deny} go={go} partners={partners} />
          ))}
        </div>
      )}

      {tab === "review" && reviewed.length > 0 && (
        <div className="radar2-reviewed">
          <button className="r2-toggle" onClick={() => setShowReviewed((v) => !v)}>
            {I.chevron({
              size: 13,
              w: 2,
              style: { transform: showReviewed ? "rotate(90deg)" : "none" },
            })}
            {showReviewed ? "Hide" : "Show"} reviewed ({reviewed.length})
          </button>
          {showReviewed && (
            <div className="sugg-list">
              {reviewed.map((s) => (
                <SuggCard
                  key={s.id}
                  s={s}
                  accept={accept}
                  deny={deny}
                  go={go}
                  reviewed
                  partners={partners}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {aiCosts && (
        <div className="radar2-costs">
          <div className="r2c-head">
            {I.ai({ size: 13, w: 1.8 })}
            <span>AI usage — last 30 days</span>
          </div>
          <div className="r2c-row">
            <span className="r2c-total mono">${aiCosts.monthly_total_usd.toFixed(3)}</span>
            <span className="r2c-calls">{aiCosts.calls} calls</span>
          </div>
          {aiCosts.by_purpose.length > 0 && (
            <div className="r2c-breakdown">
              {aiCosts.by_purpose.map((p) => (
                <div key={p.purpose} className="r2c-line">
                  <span className="r2c-purpose">{p.purpose}</span>
                  <span className="r2c-n mono">{p.calls}×</span>
                  <span className="r2c-cost mono">${Number(p.cost).toFixed(3)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
